import {
	FormModelFieldSubForm,
	isValidLeadLengthField,
	isValidOwnerField,
	PoleFieldIds,
	isValidDefaultOwnerField,
	IkePoleForemanFieldIds,
	FormModelFieldNestedList,
} from '@ikegps/core-shared';
import { m, type MithrilVNode } from '../../../../mithril';
import { SelectInputState } from '../../../../components/input/input.select';
import { IkeInputStateGroup } from '../../../../components/input/input.state.group';
import { action } from 'mobx';
import { T, Translate } from '../../../../i18n';
import { MobxState } from '../../../../state/mobx';
import { addStyles } from '../../../../components/theme';
import { Style, SubFormTypeEditorCSS } from './form.field.editor.subform.style';
import { SubformEditorDropdown } from './form.field.editor.subform.dropdown';
import { createMenu, selectedText, stateError } from './form.field.editor.subform.type';
import type { MenuComponent } from '../../../../components/menu/menu';

addStyles(Style);
export class AnchorSubFormTypeEditor {
	private leadLengthFieldSelectState: SelectInputState;
	private ownerFieldSelectState: SelectInputState;

	private leadLengthMenu: MenuComponent;
	private ownerMenu: MenuComponent;

	private reactions: MobxState;

	private _allowCreateNewOwnerField: boolean = false;

	constructor(
		private field: FormModelFieldSubForm,
		private stateGroup: IkeInputStateGroup
	) {
		this.reactions = new MobxState();

		const anchorState = this.field.subFormAnchorState;

		this.leadLengthFieldSelectState = this.createLeadLengthFieldSelect(anchorState?.leadLengthFieldId ?? null);
		this.ownerFieldSelectState = this.createOwnerFieldSelect(anchorState?.ownerFieldId ?? null);

		this.leadLengthFieldSelectState.isRequired = true;
		this.ownerFieldSelectState.isRequired = true;

		this.leadLengthMenu = createMenu(this.leadLengthFieldSelectState);
		this.ownerMenu = createMenu(this.ownerFieldSelectState);

		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidLeadLengthField)
					.map((f) => f.displayName)
					.join(','),
			() => this.updateLeadLengthFieldOptions()
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidOwnerField)
					.map((f) => f.displayName)
					.join(','),
			() => this.updateOwnerFieldOptions()
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidOwnerField)
					.map((f) => {
						return JSON.stringify((f as FormModelFieldNestedList).options);
					})
					.join(','),
			() => {
				this.updateAllowCreateNewOwnerField();
			}
		);

		this.updateAllowCreateNewOwnerField();

		this.leadLengthFieldSelectState.onValue((value) => {
			if (value) {
				this.field.Mutator.anchor.selectLeadLengthField(value);
			}
		});
		this.ownerFieldSelectState.onValue((value) => {
			if (value) {
				this.ownerFieldSelectState.setError(null);
				this.field.Mutator.anchor.selectOwnerField(value);
			} else {
				this.ownerFieldSelectState.setError(Translate(T.INPUT_REQUIRED));
				this.ownerFieldSelectState.setTouched();
			}
		});
	}

	private createLeadLengthFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter(isValidLeadLengthField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		const state = new SelectInputState(Translate(T.ANCHOR_LEAD_LENGTH_FIELD), options);
		state.setValue(currentValue);
		this.stateGroup.add(state);
		state.isLabelShown = true;

		return state;
	}

	allowCreateNewOwnerField(): boolean {
		return this._allowCreateNewOwnerField;
	}

	@action private updateAllowCreateNewOwnerField() {
		const fieldWithOwnerFieldId = this.field.fields.filter(
			(field) =>
				(field.id === PoleFieldIds.ANCHOR.OWNER || field.id === IkePoleForemanFieldIds.ANCHOR_OWNER) &&
				isValidOwnerField(field)
		);
		const validDefaultOwnerAvailable = this.field.fields.find(isValidDefaultOwnerField);
		this._allowCreateNewOwnerField = fieldWithOwnerFieldId.length === 0 && !validDefaultOwnerAvailable;
	}

	private createOwnerFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter(isValidOwnerField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		const state = new SelectInputState(Translate(T.ANCHOR_OWNER_FIELD), options);
		const valueInOptions = currentValue !== null && options.some((opt) => opt.value === currentValue);
		if (valueInOptions) {
			state.setValue(currentValue);
		} else {
			state.setError(Translate(T.INPUT_REQUIRED));
			state.setTouched();
		}
		this.stateGroup.add(state);
		state.isLabelShown = true;

		return state;
	}

	@action private updateLeadLengthFieldOptions() {
		if (!this.leadLengthFieldSelectState) return;

		const options = this.field.fields
			.filter(isValidLeadLengthField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		this.leadLengthFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === this.leadLengthFieldSelectState.value)) {
			this.leadLengthFieldSelectState.setValue(null);
		}
	}

	@action private updateOwnerFieldOptions() {
		if (!this.ownerFieldSelectState) return;

		const options = this.field.fields
			.filter(isValidOwnerField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		this.ownerFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === this.ownerFieldSelectState.value)) {
			this.ownerFieldSelectState.setValue(null);
		}
	}

	@action createOwnerField() {
		const leadLengthIndex = this.field.fields.findIndex(isValidLeadLengthField);
		const insertIndex = leadLengthIndex >= 0 ? leadLengthIndex : 2;
		const field = this.field.Mutator.anchor.addOwnerField(insertIndex);

		const options = this.field.fields.filter(isValidOwnerField).map((f) => ({ key: f.displayName, value: f.id }));
		this.ownerFieldSelectState.setOptions(options);
		this.ownerFieldSelectState.setValue(field.id);
		this.ownerFieldSelectState.setError(null);
	}

	view(): MithrilVNode {
		return m('div', { className: SubFormTypeEditorCSS.wrapper }, [
			m('hr'),
			m('div', { className: SubFormTypeEditorCSS.grid }, [
				SubformEditorDropdown.view({
					title: Translate(T.ANCHOR_LEAD_LENGTH_FIELD),
					selectedText: selectedText(this.leadLengthFieldSelectState),
					menu: this.leadLengthMenu,
				}),
				SubformEditorDropdown.view({
					title: Translate(T.ANCHOR_OWNER_FIELD),
					selectedText: selectedText(this.ownerFieldSelectState),
					menu: this.ownerMenu,
					error: stateError(this.ownerFieldSelectState),
					actionBtn: this.allowCreateNewOwnerField()
						? { action: () => this.createOwnerField(), btnText: Translate(T.FORM_FIELD_CREATE) }
						: undefined,
				}),
			]),
		]);
	}

	destroy() {
		this.reactions.destroy();

		// Remove select states from the parent state group
		this.stateGroup.remove(this.leadLengthFieldSelectState);
		this.stateGroup.remove(this.ownerFieldSelectState);
	}
}
